import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { brandCopy } from '../constants/brand';
import { invalidateOrder } from './invalidateOrder';
import {
    AskCarlFeedbackDetails,
    AskCarlFeedbackRating,
    AskCarlInputRequest,
    AskCarlMessage,
    AskCarlThread,
    AskCarlToolEvent,
} from '../types/askCarl';

/**
 * Chat transport for Ask Carl, speaking the eve agent's session protocol.
 *
 * The eve runtime is served same-origin under /eve/v1/* — proxied by the CRA
 * dev server locally (src/setupProxy.js) and by a Vercel rewrite in production
 * (vercel.json).
 *
 * Conversations are organized into named threads (ENG-1713): each thread maps
 * to one durable eve session (sessionId + continuationToken). Threads get an
 * instant placeholder title from the first message, then a model-written
 * synopsis (POST /carl/thread-title) once the first turn completes.
 *
 * Thread persistence (ENG-1712): the thread index + a trimmed message snapshot
 * live server-side in partbay-api's carlThreads collection (/carl/threads,
 * user-scoped) so threads follow the user across devices. localStorage is an
 * optimistic cache in front of that; the authoritative conversation history
 * lives in the eve durable session either way.
 *
 * Tool substeps (ENG-1711) stream live: actions.requested adds a running step,
 * action.result flips it to completed/failed, and the UI renders the timeline
 * while the turn runs.
 */

interface UseAskCarlChatReturn {
    messages: AskCarlMessage[];
    isLoading: boolean;
    error: string | null;
    sendMessage: (query: string) => Promise<void>;
    clearMessages: () => void;
    latestUiPayload: Record<string, unknown> | undefined;
    pendingInputRequests: AskCarlInputRequest[];
    respondToInput: (requestId: string, response: { optionId?: string; text?: string }) => Promise<void>;
    threads: AskCarlThread[];
    activeThreadId: string | null;
    startNewThread: () => void;
    selectThread: (threadId: string) => void;
    deleteThread: (threadId: string) => void;
    renameThread: (threadId: string, title: string) => void;
    /** Select (or create) the thread scoped to an order — used by the OrderDetail Carl drawer. */
    ensureOrderThread: (order: { orderId: string; roNumber?: string; vehicleName?: string }) => void;
    /** Always start a fresh thread scoped to an order (drawer "New thread"). */
    startNewOrderThread: (order: { orderId: string; roNumber?: string; vehicleName?: string }) => void;
    /** Rate an assistant response; thumbs-down reports the thread to Slack for review. */
    submitFeedback: (messageId: string, rating: AskCarlFeedbackRating, details?: AskCarlFeedbackDetails) => Promise<boolean>;
}

interface AskCarlStoredMessage extends Omit<AskCarlMessage, 'timestamp'> {
    timestamp: string;
}

/** Friendly step labels shown while a tool call runs (ENG-1711). */
const TOOL_STEP_LABELS: Record<string, string> = {
    resolve_order_context: 'Looking up the order…',
    list_orders: 'Fetching orders…',
    search_orders: 'Searching…',
    query_orders: 'Querying orders…',
    get_part_options: 'Fetching part options…',
    get_workflow_status: 'Checking automation status…',
    get_settings_context: 'Reading organization settings…',
    get_ems_context: 'Looking up EMS record…',
    get_supplier_messages: 'Reading supplier thread…',
    attempt_rekey: 'Attempting re-key…',
    skip_rekey: 'Skipping re-key…',
    resend_ems: 'Re-sending EMS to marketplaces…',
    update_ems_location: 'Updating EMS location…',
    change_part_selection: 'Changing part selection…',
    place_order: 'Placing order…',
    update_order_status: 'Updating order status…',
    toggle_part_flag: 'Toggling part flag…',
    change_part_exclusion: 'Updating part exclusion…',
    bulk_change_part_exclusion: 'Updating part exclusions…',
    set_quotes_only: 'Updating quotes-only…',
    request_quotes: 'Requesting quotes…',
    cancel_quote_request: 'Cancelling quote request…',
    cancel_scheduled_order_placement: 'Cancelling scheduled order placement…',
    disable_auto_order: 'Updating auto-order…',
    cancel_return_order: 'Processing cancel/return…',
    skip_quoting: 'Skipping quoting…',
    confirm_all_selections: 'Confirming all selections…',
    reset_to_partbay_selections: brandCopy('Resetting to PartBay selections…'),
    export_selections: 'Exporting selections…',
    add_part_note: 'Adding part note…',
    manage_assembly_mapping: 'Updating assembly mapping…',
    accept_supplier_changes: 'Accepting supplier changes…',
    attempt_part_number_change: 'Attempting part-number change…',
    update_order_metadata: 'Updating order details…',
    send_order_message: 'Posting to order chat…',
    send_supplier_message: 'Messaging supplier…',
    support: 'Investigating with support specialist…',
    load_skill: 'Loading troubleshooting playbook…',
    ask_question: 'Asking for your input…',
    get_reporting_overview: 'Pulling reporting overview…',
    get_reporting_breakdown: 'Crunching reporting numbers…',
    get_reporting_trend: 'Charting the trend…',
    get_reporting_productivity: 'Reviewing team productivity…',
    get_invoicing_reporting: 'Pulling invoicing stats…',
};

export const toolStepLabel = (toolName: string): string =>
    TOOL_STEP_LABELS[toolName] ?? `Running ${toolName.replace(/_/g, ' ')}…`;

/**
 * Shown in place of ANY raw failure detail (eve/Bedrock/tool errors). Raw
 * messages are internal and often alarming ("ThrottlingException…"), so they
 * go to console.error only.
 */
const GENERIC_ERROR_MESSAGE = 'An error occurred. Please try again later.';

/** Translate a tool's `ui` payload into the keys the AskCarl page renders. */
const mapToolUiPayload = (ui: Record<string, unknown>): Record<string, unknown> | null => {
    const type = ui.type as string | undefined;
    if (type === 'order-detail' && ui.detail) {
        return { orderDetail: { ...(ui.detail as Record<string, unknown>), orderId: ui.orderId } };
    }
    if (type === 'ems-detail' && ui.detail) {
        return { emsDetail: { ...(ui.detail as Record<string, unknown>), emsId: ui.emsId } };
    }
    // Only the pre-submit form is interactive; a submitted quote is confirmed in text.
    if (type === 'quote-form' && ui.quoteModal) {
        return { submitQuoteModal: { ...(ui.quoteModal as Record<string, unknown>), orderId: ui.orderId } };
    }
    if (type === 'part-options' && ui.data) {
        return { partOptions: ui.data };
    }
    if (type === 'ems-export-history' && Array.isArray(ui.exports)) {
        return { emsExportHistory: { orderId: ui.orderId, order: ui.order, exports: ui.exports } };
    }
    if (type === 'orders-table' && Array.isArray(ui.orders)) {
        return { ordersTable: { orders: ui.orders, hasMore: ui.hasMore, query: ui.query } };
    }
    // Reporting chart/table card (ENG-2169) from the permission-gated reporting tools.
    if (type === 'reporting-card' && ui.report) {
        return { reportingCard: ui.report };
    }
    return null;
};

/**
 * eve may stream `toModelOutput` in action.result without the tool's `ui`
 * envelope — synthesize the envelope from the output shape when needed.
 */
const inferToolUiEnvelope = (output: Record<string, unknown>): Record<string, unknown> | undefined => {
    if (output.ui && typeof output.ui === 'object') {
        return output.ui as Record<string, unknown>;
    }
    if (output.options && typeof output.options === 'object' && output.orderPartId) {
        return {
            type: 'part-options',
            orderId: output.orderId,
            orderPartId: output.orderPartId,
            data: output.options,
        };
    }
    if (output.detail && typeof output.detail === 'object' && output.orderId) {
        return { type: 'order-detail', orderId: output.orderId, detail: output.detail };
    }
    if (output.ems && typeof output.ems === 'object' && output.emsId) {
        return { type: 'ems-detail', emsId: output.emsId, detail: output.ems };
    }
    if (Array.isArray(output.exports) && output.orderId) {
        return { type: 'ems-export-history', orderId: output.orderId, order: output.order, exports: output.exports };
    }
    if (output.quoteModal && output.orderId) {
        return { type: 'quote-form', orderId: output.orderId, quoteModal: output.quoteModal };
    }
    // Reporting tools return { report: { kind, ... } } (ENG-2169).
    if (output.report && typeof output.report === 'object'
        && typeof (output.report as Record<string, unknown>).kind === 'string') {
        return { type: 'reporting-card', report: output.report };
    }
    // query_orders (orders + resolved) and list_orders (orders + feed) both
    // render the shared orders table card. No `query` here on purpose: only the
    // tool's ui envelope (handled above) carries it, and neither toModelOutput
    // exposes enough to rebuild it — reconstructing a partial one would page a
    // different result set, so the card shows page 1 without Load more instead.
    if (Array.isArray(output.orders)
        && ((output.resolved && typeof output.resolved === 'object') || typeof output.feed === 'string')) {
        return { type: 'orders-table', orders: output.orders, hasMore: output.hasMore };
    }
    return undefined;
};

type EveStreamEvent = { type?: string; data?: Record<string, unknown>; meta?: { at?: string } };

const mapStreamInputRequests = (requests: Array<Record<string, unknown>>): AskCarlInputRequest[] =>
    requests.map((request) => {
        const action = request.action as Record<string, unknown> | undefined;
        return {
            requestId: String(request.requestId),
            prompt: String(request.prompt ?? 'Approve this action?'),
            display: request.display as AskCarlInputRequest['display'],
            options: request.options as AskCarlInputRequest['options'],
            allowFreeform: request.allowFreeform === true,
            toolName: action?.toolName as string | undefined,
            toolInput: action?.input as Record<string, unknown> | undefined,
        };
    });

/** Fold a full eve session replay into chat messages (authoritative server-side history). */
const foldEveEventsToMessages = (events: EveStreamEvent[]): {
    messages: AskCarlMessage[];
    pendingInputRequests: AskCarlInputRequest[];
    lastEventAt: number;
} => {
    const messages: AskCarlMessage[] = [];
    let toolEvents: AskCarlToolEvent[] = [];
    let ui: Record<string, unknown> | undefined;
    let content = '';
    let inputRequests: AskCarlInputRequest[] | undefined;
    let pendingInputRequests: AskCarlInputRequest[] = [];
    let lastEventAt = 0;

    const flushAssistant = () => {
        if (!content && !toolEvents.length && !ui && !inputRequests?.length) return;
        messages.push({
            id: uuidv4(),
            role: 'assistant',
            content,
            timestamp: new Date(),
            toolEvents: toolEvents.length ? [...toolEvents] : undefined,
            ui,
            inputRequests,
        });
        toolEvents = [];
        ui = undefined;
        content = '';
        inputRequests = undefined;
    };

    const sorted = [...events].sort((a, b) => {
        const ta = a.meta?.at ? Date.parse(a.meta.at) : 0;
        const tb = b.meta?.at ? Date.parse(b.meta.at) : 0;
        return ta - tb;
    });

    for (const event of sorted) {
        const recordedAt = event.meta?.at ? Date.parse(event.meta.at) : NaN;
        if (!Number.isNaN(recordedAt) && recordedAt > lastEventAt) lastEventAt = recordedAt;

        const data = (event.data ?? {}) as Record<string, unknown>;

        switch (event.type) {
            case 'message.received': {
                flushAssistant();
                // A new user message supersedes any still-open question.
                pendingInputRequests = [];
                const text = data.message;
                if (typeof text === 'string' && text.trim()) {
                    messages.push({
                        id: uuidv4(),
                        role: 'user',
                        content: text.trim(),
                        timestamp: Number.isNaN(recordedAt) ? new Date() : new Date(recordedAt),
                    });
                }
                break;
            }
            case 'actions.requested': {
                const actions = (data.actions as Array<Record<string, unknown>>) ?? [];
                for (const action of actions) {
                    const kind = action.kind as string;
                    const name = (action.toolName ?? action.subagentName ?? action.name ?? kind) as string | undefined;
                    if (!name || kind === 'load-skill') continue;
                    const callId = action.callId as string | undefined;
                    if (callId && toolEvents.some((step) => step.callId === callId)) continue;
                    toolEvents.push({
                        toolName: name,
                        callId,
                        timestamp: new Date().toISOString(),
                        summary: toolStepLabel(name),
                        status: 'running',
                    });
                }
                break;
            }
            case 'action.result': {
                const result = data.result as Record<string, unknown> | undefined;
                const callId = result?.callId as string | undefined;
                const failed = data.status === 'failed' || result?.isError === true || Boolean(data.error);
                const step = toolEvents.find((toolEvent) => toolEvent.callId && toolEvent.callId === callId);
                if (step) step.status = failed ? 'failed' : 'completed';

                const output = result?.output as Record<string, unknown> | undefined;
                if (output && typeof output === 'object') {
                    const envelope = inferToolUiEnvelope(output);
                    if (envelope) {
                        const mapped = mapToolUiPayload(envelope);
                        if (mapped) ui = { ...(ui ?? {}), ...mapped };
                    }
                }
                break;
            }
            case 'subagent.completed': {
                const step = toolEvents.find((toolEvent) => toolEvent.toolName === 'support' && toolEvent.status === 'running');
                if (step) step.status = 'completed';
                break;
            }
            case 'message.completed': {
                const text = data.message;
                if (typeof text === 'string' && text) content = text;
                break;
            }
            case 'input.requested': {
                const requests = (data.requests as Array<Record<string, unknown>>) ?? [];
                const mapped = mapStreamInputRequests(requests);
                if (mapped.length) {
                    inputRequests = mapped;
                    pendingInputRequests = mapped;
                    for (const step of toolEvents) {
                        if (step.toolName === 'ask_question' && step.status === 'running') step.status = 'completed';
                    }
                }
                break;
            }
            case 'turn.started':
                // A new turn beginning means any prior question was answered or
                // superseded. An unanswered ask_question, by contrast, is the
                // LAST thing in the stream, so it survives to the end.
                pendingInputRequests = [];
                break;
            case 'turn.completed':
                for (const step of toolEvents) {
                    if (step.status === 'running') step.status = 'completed';
                }
                flushAssistant();
                // NOTE: do NOT clear pendingInputRequests here. An ask_question
                // parks the turn — eve emits input.requested then turn.completed
                // while still awaiting the user (no session.waiting follows) — so
                // the question must survive the turn boundary. It's cleared when
                // the next turn starts (or a new user message arrives).
                break;
            case 'session.waiting':
                for (const step of toolEvents) {
                    if (step.status === 'running') step.status = 'completed';
                }
                flushAssistant();
                break;
            case 'session.completed':
                for (const step of toolEvents) {
                    if (step.status === 'running') step.status = 'completed';
                }
                flushAssistant();
                pendingInputRequests = [];
                break;
            case 'turn.failed':
            case 'session.failed': {
                content = content || GENERIC_ERROR_MESSAGE;
                for (const step of toolEvents) {
                    if (step.status === 'running') step.status = 'failed';
                }
                flushAssistant();
                pendingInputRequests = [];
                break;
            }
            default:
                break;
        }
    }

    for (const step of toolEvents) {
        if (step.status === 'running') step.status = 'completed';
    }
    flushAssistant();

    return { messages, pendingInputRequests, lastEventAt };
};

/**
 * Instant placeholder title shown while the model-written synopsis is being
 * generated (POST /carl/thread-title, Bedrock Sonnet). Just a truncation of
 * the first question — the real title swaps in when the turn completes.
 */
const summarizeTitle = (query: string): string => {
    const clean = query.trim().replace(/\s+/g, ' ');
    return clean.length > 60 ? `${clean.slice(0, 57)}…` : clean;
};

// ── Server-side thread persistence (ENG-1712) ──────────────────────────────

/** Newest messages kept in the server snapshot (matches the API's cap). */
const THREAD_SNAPSHOT_LIMIT = 50;

/** Wire shape of a thread row in partbay-api's carlThreads collection. */
interface ServerCarlThread {
    threadId: string;
    title: string;
    titleEdited?: boolean;
    orderId?: string;
    roNumber?: string;
    eveSessionId?: string;
    continuationToken?: string;
    messagesSnapshot?: AskCarlStoredMessage[];
    createdAt: string;
    updatedAt: string;
}

/**
 * Trim a message for the server snapshot: drop the rich `ui` payloads and
 * per-step tool payloads (they can be hundreds of KB of order detail). On
 * another device, reconcileThreadFromEve replays the eve session and rebuilds
 * the cards; the snapshot only needs to paint text + step labels instantly.
 */
const toSnapshotMessage = (message: AskCarlStoredMessage): AskCarlStoredMessage => ({
    ...message,
    ui: undefined,
    toolEvents: message.toolEvents?.map(({ payload: _payload, ...step }) => step),
});

/**
 * PUT body for a thread. Also used as the sync fingerprint: a thread is only
 * re-PUT when this serialization changes.
 */
const buildThreadPutBody = (thread: AskCarlThread, snapshot: AskCarlStoredMessage[]): string =>
    JSON.stringify({
        title: thread.title,
        titleEdited: thread.titleEdited,
        orderId: thread.orderId,
        roNumber: thread.roNumber,
        eveSessionId: thread.sessionId,
        continuationToken: thread.continuationToken,
        createdAt: thread.createdAt,
        updatedAt: thread.updatedAt,
        messagesSnapshot: snapshot.slice(-THREAD_SNAPSHOT_LIMIT).map(toSnapshotMessage),
    });

const fromServerThread = (remote: ServerCarlThread): AskCarlThread => ({
    threadId: remote.threadId,
    title: remote.title,
    titleEdited: remote.titleEdited,
    orderId: remote.orderId,
    roNumber: remote.roNumber,
    sessionId: remote.eveSessionId,
    continuationToken: remote.continuationToken,
    createdAt: remote.createdAt,
    updatedAt: remote.updatedAt,
});

/**
 * Only a thread with something durable syncs to the server: an eve session, a
 * user-authored title, or at least one message. Order-scoped threads used to
 * pass on `orderId`/title alone, so merely opening the order drawer PUT an
 * empty "RO … · vehicle" row into carlThreads — one per drawer open that never
 * sent a message (the spurious-empty-thread flood).
 */
const isSyncableThread = (thread: AskCarlThread, snapshot: AskCarlStoredMessage[]): boolean =>
    Boolean(thread.sessionId || thread.titleEdited || snapshot.length > 0);

/**
 * Synchronous first read of the thread index. Hydrating only in an effect left
 * the whole mount effect pass staring at an empty list, so the order drawer's
 * mount-time ensureOrderThread missed the order's existing thread and minted a
 * duplicate. The effect-based hydration still runs after this (it prunes
 * blanks, migrates legacy storage, and handles the user arriving later).
 */
const readStoredThreads = (userId: string | null): AskCarlThread[] => {
    if (!userId || typeof window === 'undefined') return [];
    try {
        const parsed = JSON.parse(window.localStorage.getItem(`ask-carl:threads:${userId}`) ?? '[]');
        return Array.isArray(parsed) ? (parsed as AskCarlThread[]) : [];
    } catch {
        return [];
    }
};

/**
 * A snapshot caught mid-stream: its last message is an assistant turn that has
 * registered tool steps but no answer text yet. Persisting this would overwrite
 * the durable copy with a blank answer (the bug that wiped a thread's content),
 * so the debounced sync skips it and waits for the completion flush to write the
 * settled turn instead.
 */
const isIncompleteSnapshot = (snapshot: AskCarlStoredMessage[]): boolean => {
    const last = snapshot[snapshot.length - 1];
    return Boolean(
        last
        && last.role === 'assistant'
        && !last.content?.trim()
        && (last.toolEvents?.length ?? 0) > 0,
    );
};

/**
 * A pending request is a tool-approval gate (Approve/Deny) rather than a
 * question. eve only resolves these via `optionId: 'approve' | 'deny'` — a
 * plain typed message makes it defer and re-emit the same gate, so the UI
 * must route the user through the approval buttons, not the composer.
 */
export const isApprovalRequest = (request: AskCarlInputRequest): boolean =>
    request.display === 'confirmation'
    || (!request.options?.length && request.toolName !== 'ask_question' && request.allowFreeform !== true);

export const useAskCarlChat = (): UseAskCarlChatReturn => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();
    const userId = sessionContext?.userId ?? null;

    const [threads, setThreads] = useState<AskCarlThread[]>(() => readStoredThreads(userId));
    const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
    const [messages, setMessages] = useState<AskCarlMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pendingInputRequests, setPendingInputRequests] = useState<AskCarlInputRequest[]>([]);

    const streamAbortRef = useRef<AbortController | null>(null);
    const reconcileAbortRef = useRef<AbortController | null>(null);
    /**
     * Max event timestamp (meta.at, server clock) folded in per eve session.
     * The stream endpoint replays the full durable history on every attach;
     * resuming with only a wall-clock cushion re-ingests the just-answered
     * approval request (re-rendering the card and parking the reader before
     * the rest of the turn arrives), so resume turns filter from here instead.
     */
    const lastEventAtRef = useRef<Map<string, number>>(new Map());
    /** Input requests already answered this session — replays of them are ignored. */
    const answeredRequestIdsRef = useRef<Set<string>>(new Set());
    const threadsRef = useRef<AskCarlThread[]>([]);
    threadsRef.current = threads;
    const activeThreadIdRef = useRef<string | null>(null);
    activeThreadIdRef.current = activeThreadId;
    const pendingInputRef = useRef<AskCarlInputRequest[]>([]);
    pendingInputRef.current = pendingInputRequests;
    const messagesRef = useRef<AskCarlMessage[]>([]);
    messagesRef.current = messages;

    const threadsKey = userId ? `ask-carl:threads:${userId}` : null;
    const activeThreadKey = userId ? `ask-carl:active-thread:${userId}` : null;
    const messagesKey = useCallback(
        (threadId: string) => `ask-carl:messages:${userId}:${threadId}`,
        [userId],
    );

    /** threadId → last PUT body successfully queued, so unchanged threads aren't re-sent. */
    const syncedThreadBodiesRef = useRef<Map<string, string>>(new Map());
    const syncTimerRef = useRef<number | null>(null);
    /**
     * Thread just created inside sendMessage: its first user message lives
     * only in state (nothing persisted yet), so the activeThreadId-change
     * effect must NOT reload (empty) localStorage over it or abort its
     * just-started stream.
     */
    const skipMessagesLoadRef = useRef<string | null>(null);

    // ── Hydration ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!threadsKey || typeof window === 'undefined') {
            setThreads([]);
            setActiveThreadId(null);
            setMessages([]);
            return;
        }
        try {
            let hydrated: AskCarlThread[] = JSON.parse(window.localStorage.getItem(threadsKey) ?? '[]');

            // Migrate pre-thread storage (single conversation per user)
            const legacyMessagesRaw = window.localStorage.getItem(`ask-carl:messages:${userId}`);
            if (legacyMessagesRaw && hydrated.length === 0) {
                const legacyMessages = JSON.parse(legacyMessagesRaw) as AskCarlStoredMessage[];
                const firstUser = legacyMessages.find((message) => message.role === 'user');
                if (legacyMessages.length) {
                    const legacySessionRaw = window.localStorage.getItem(`ask-carl:eve-session:${userId}`);
                    const legacySession = legacySessionRaw ? JSON.parse(legacySessionRaw) : null;
                    const migrated: AskCarlThread = {
                        threadId: uuidv4(),
                        title: firstUser ? summarizeTitle(firstUser.content) : 'Earlier conversation',
                        sessionId: legacySession?.sessionId,
                        continuationToken: legacySession?.continuationToken,
                        createdAt: legacyMessages[0]?.timestamp ?? new Date().toISOString(),
                        updatedAt: legacyMessages[legacyMessages.length - 1]?.timestamp ?? new Date().toISOString(),
                    };
                    hydrated = [migrated];
                    window.localStorage.setItem(`ask-carl:messages:${userId}:${migrated.threadId}`, legacyMessagesRaw);
                }
                window.localStorage.removeItem(`ask-carl:messages:${userId}`);
                window.localStorage.removeItem(`ask-carl:eve-session:${userId}`);
                window.localStorage.setItem(threadsKey, JSON.stringify(hydrated));
            }

            const savedActive = activeThreadKey ? window.localStorage.getItem(activeThreadKey) : null;
            const active = hydrated.find((thread) => thread.threadId === savedActive)?.threadId
                ?? hydrated[0]?.threadId
                ?? null;

            // Collapse leftover blank "New conversation" threads down to (at
            // most) the active one so history doesn't fill with empties.
            const isBlank = (thread: AskCarlThread) => {
                if (thread.sessionId || thread.orderId || thread.titleEdited) return false;
                if (thread.title !== 'New conversation') return false;
                const raw = window.localStorage.getItem(`ask-carl:messages:${userId}:${thread.threadId}`);
                return !raw || raw === '[]';
            };
            const pruned = hydrated.filter((thread) => {
                if (thread.threadId === active) return true;
                if (isBlank(thread)) {
                    window.localStorage.removeItem(`ask-carl:messages:${userId}:${thread.threadId}`);
                    return false;
                }
                return true;
            });
            if (pruned.length !== hydrated.length) {
                window.localStorage.setItem(threadsKey, JSON.stringify(pruned));
            }

            setThreads(pruned);
            setActiveThreadId(active);
        } catch (hydrationError) {
            console.error('Failed to hydrate Ask Carl threads:', hydrationError);
            setThreads([]);
            setActiveThreadId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [threadsKey, activeThreadKey, userId]);

    // Load messages when the active thread changes
    useEffect(() => {
        // Thread created mid-send: the user's first message is already in
        // state and the turn is in flight — reloading localStorage here would
        // blank the conversation and abort the stream (the "sent but landed on
        // an empty new-thread screen" bug).
        if (activeThreadId && skipMessagesLoadRef.current === activeThreadId) {
            skipMessagesLoadRef.current = null;
            if (activeThreadKey && typeof window !== 'undefined') {
                window.localStorage.setItem(activeThreadKey, activeThreadId);
            }
            return;
        }
        streamAbortRef.current?.abort();
        reconcileAbortRef.current?.abort();
        setPendingInputRequests([]);
        setError(null);
        if (!activeThreadId || !userId || typeof window === 'undefined') {
            setMessages([]);
            return;
        }
        try {
            const raw = window.localStorage.getItem(messagesKey(activeThreadId));
            const parsed = raw ? (JSON.parse(raw) as AskCarlStoredMessage[]) : [];
            setMessages(parsed.map((message) => ({ ...message, timestamp: new Date(message.timestamp) })));
            // Restore an unanswered question/approval from the last assistant message
            const last = parsed[parsed.length - 1];
            if (last?.role === 'assistant' && last.inputRequests?.length) {
                setPendingInputRequests(last.inputRequests);
            }
        } catch {
            setMessages([]);
        }
        if (activeThreadKey) window.localStorage.setItem(activeThreadKey, activeThreadId);
    }, [activeThreadId, activeThreadKey, messagesKey, userId]);

    // Persist messages for the active thread
    useEffect(() => {
        if (!activeThreadId || !userId || typeof window === 'undefined') return;
        try {
            const serialized: AskCarlStoredMessage[] = messages.map((message) => ({
                ...message,
                timestamp: message.timestamp.toISOString(),
            }));
            window.localStorage.setItem(messagesKey(activeThreadId), JSON.stringify(serialized));
        } catch (persistError) {
            console.error('Failed to persist Ask Carl messages:', persistError);
        }
    }, [messages, activeThreadId, messagesKey, userId]);

    const persistThreads = useCallback((updater: (previous: AskCarlThread[]) => AskCarlThread[]) => {
        setThreads((previous) => {
            const next = updater(previous);
            if (threadsKey && typeof window !== 'undefined') {
                window.localStorage.setItem(threadsKey, JSON.stringify(next));
            }
            return next;
        });
    }, [threadsKey]);

    const updateThread = useCallback((threadId: string, patch: Partial<AskCarlThread>) => {
        persistThreads((previous) =>
            previous.map((thread) => (thread.threadId === threadId ? { ...thread, ...patch } : thread)),
        );
    }, [persistThreads]);

    // Merge the server-side thread index (carlThreads via partbay-api) over
    // the localStorage cache: server wins on newer updatedAt, local-only
    // threads survive and get pushed by the sync effect below. This is what
    // makes threads follow the user across browsers/devices (ENG-1712).
    useEffect(() => {
        if (!userId || !sessionContext?.sessionId || typeof window === 'undefined') return undefined;
        const authHeader = sessionContext.sessionId;
        let cancelled = false;

        (async () => {
            try {
                const response = await fetch(`${API_URL}/carl/threads`, {
                    headers: { Authorization: authHeader },
                });
                if (!response.ok || cancelled) return;
                const body = await response.json() as { threads?: ServerCarlThread[] };
                const remote = body.threads ?? [];
                if (cancelled || !remote.length) return;

                // Seed message snapshots for threads this device has never seen.
                for (const entry of remote) {
                    const key = messagesKey(entry.threadId);
                    if (entry.messagesSnapshot?.length && !window.localStorage.getItem(key)) {
                        window.localStorage.setItem(key, JSON.stringify(entry.messagesSnapshot));
                    }
                }

                // Server rows are in sync by definition — don't immediately re-PUT them.
                for (const entry of remote) {
                    syncedThreadBodiesRef.current.set(
                        entry.threadId,
                        buildThreadPutBody(fromServerThread(entry), entry.messagesSnapshot ?? []),
                    );
                }

                persistThreads((previous) => {
                    const byId = new Map(previous.map((thread) => [thread.threadId, thread] as const));
                    for (const entry of remote) {
                        const local = byId.get(entry.threadId);
                        if (!local || (entry.updatedAt ?? '') >= (local.updatedAt ?? '')) {
                            byId.set(entry.threadId, fromServerThread(entry));
                        }
                    }
                    return Array.from(byId.values())
                        .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
                });
            } catch (fetchError) {
                // Offline / API hiccup: the localStorage cache keeps working.
                console.error('Ask Carl thread fetch failed:', fetchError);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [userId, sessionContext?.sessionId, messagesKey, persistThreads]);

    // PUT one thread's index + trimmed snapshot to carlThreads. Deduped on the
    // serialized body so unchanged threads aren't re-sent.
    const putThreadSnapshot = useCallback((thread: AskCarlThread, snapshot: AskCarlStoredMessage[], authHeader: string) => {
        const body = buildThreadPutBody(thread, snapshot);
        if (syncedThreadBodiesRef.current.get(thread.threadId) === body) return;
        syncedThreadBodiesRef.current.set(thread.threadId, body);
        fetch(`${API_URL}/carl/threads/${thread.threadId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: authHeader },
            body,
        }).then((response) => {
            // Retry on the next change tick rather than dropping the update.
            if (!response.ok) syncedThreadBodiesRef.current.delete(thread.threadId);
        }).catch((syncError) => {
            syncedThreadBodiesRef.current.delete(thread.threadId);
            console.error('Ask Carl thread sync failed:', syncError);
        });
    }, []);

    // Push changed threads (index + trimmed message snapshot) to the server,
    // debounced. localStorage stays the fast optimistic cache; the server copy
    // is what other devices hydrate from.
    useEffect(() => {
        if (!userId || !sessionContext?.sessionId || typeof window === 'undefined') return undefined;
        const authHeader = sessionContext.sessionId;

        if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current);
        syncTimerRef.current = window.setTimeout(() => {
            for (const thread of threadsRef.current) {
                let snapshot: AskCarlStoredMessage[] = [];
                try {
                    const raw = window.localStorage.getItem(messagesKey(thread.threadId));
                    if (raw) snapshot = JSON.parse(raw) as AskCarlStoredMessage[];
                } catch {
                    // Snapshot is best-effort; the eve session holds the real history.
                }
                if (!isSyncableThread(thread, snapshot)) continue;
                // Never persist a turn caught mid-stream — it would clobber the
                // durable snapshot with a blank answer. The completion flush
                // (below) writes the settled turn once it finishes.
                if (isIncompleteSnapshot(snapshot)) continue;
                putThreadSnapshot(thread, snapshot, authHeader);
            }
        }, 1000);

        return () => {
            if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current);
        };
    }, [threads, messages, userId, sessionContext?.sessionId, messagesKey, putThreadSnapshot]);

    // Force a durable write the instant a turn settles (isLoading → false),
    // straight from the settled messages, so a completed answer can't be lost to
    // the 1s debounce window if the user navigates away or closes the tab. This
    // bypasses the debounce but reuses the same dedupe, so it won't double-send.
    const wasLoadingRef = useRef(false);
    useEffect(() => {
        const settled = wasLoadingRef.current && !isLoading;
        wasLoadingRef.current = isLoading;
        if (!settled) return;
        if (!userId || !sessionContext?.sessionId || typeof window === 'undefined') return;
        const threadId = activeThreadIdRef.current;
        if (!threadId) return;
        const thread = threadsRef.current.find((entry) => entry.threadId === threadId);
        if (!thread) return;

        const settledMessages: AskCarlStoredMessage[] = messagesRef.current.map((message) => ({
            ...message,
            timestamp: message.timestamp.toISOString(),
        }));
        if (!isSyncableThread(thread, settledMessages)) return;
        // Keep localStorage aligned with the settled state, then push it.
        try {
            window.localStorage.setItem(messagesKey(threadId), JSON.stringify(settledMessages));
        } catch {
            // Best-effort cache write; the PUT below is what durably persists.
        }
        putThreadSnapshot(thread, settledMessages, sessionContext.sessionId);
    }, [isLoading, userId, sessionContext?.sessionId, messagesKey, putThreadSnapshot]);

    /**
     * A blank, unsent "New conversation" the user never touched — safe to reuse
     * or prune so history doesn't accumulate duplicate empty threads.
     */
    const isDisposableThread = useCallback((thread: AskCarlThread): boolean => {
        if (thread.sessionId || thread.orderId || thread.titleEdited) return false;
        if (thread.title !== 'New conversation') return false;
        if (!userId || typeof window === 'undefined') return true;
        const raw = window.localStorage.getItem(messagesKey(thread.threadId));
        return !raw || raw === '[]';
    }, [messagesKey, userId]);

    /** Drop every disposable blank thread except `keepId`. */
    const pruneDisposableThreads = useCallback((keepId?: string) => {
        persistThreads((previous) => {
            const removed: string[] = [];
            const next = previous.filter((thread) => {
                if (thread.threadId === keepId) return true;
                if (isDisposableThread(thread)) {
                    removed.push(thread.threadId);
                    return false;
                }
                return true;
            });
            if (removed.length && userId && typeof window !== 'undefined') {
                removed.forEach((id) => window.localStorage.removeItem(messagesKey(id)));
            }
            return removed.length ? next : previous;
        });
    }, [persistThreads, isDisposableThread, messagesKey, userId]);

    // ── Thread management (ENG-1713) ───────────────────────────────────────
    const startNewThread = useCallback(() => {
        streamAbortRef.current?.abort();
        const activeId = activeThreadIdRef.current;
        const activeThread = activeId ? threadsRef.current.find((thread) => thread.threadId === activeId) : undefined;

        // Already on a blank thread? Reuse it and clear any stray empties.
        if (activeThread && isDisposableThread(activeThread)) {
            pruneDisposableThreads(activeId!);
            setMessages([]);
            setPendingInputRequests([]);
            setError(null);
            return;
        }

        // Reuse an existing blank thread rather than stacking another empty.
        const existingBlank = threadsRef.current.find((thread) => isDisposableThread(thread));
        if (existingBlank) {
            pruneDisposableThreads(existingBlank.threadId);
            updateThread(existingBlank.threadId, { updatedAt: new Date().toISOString() });
            setActiveThreadId(existingBlank.threadId);
            setMessages([]);
            setPendingInputRequests([]);
            setError(null);
            return;
        }

        const thread: AskCarlThread = {
            threadId: uuidv4(),
            title: 'New conversation',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
        persistThreads((previous) => [thread, ...previous]);
        setActiveThreadId(thread.threadId);
        setMessages([]);
        setPendingInputRequests([]);
        setError(null);
    }, [isDisposableThread, pruneDisposableThreads, updateThread, persistThreads]);

    const selectThread = useCallback((threadId: string) => {
        if (threadId === activeThreadIdRef.current) return;
        setActiveThreadId(threadId);
    }, []);

    const deleteThread = useCallback((threadId: string) => {
        persistThreads((previous) => previous.filter((thread) => thread.threadId !== threadId));
        if (userId && typeof window !== 'undefined') {
            window.localStorage.removeItem(messagesKey(threadId));
        }
        // Remove server-side too so the thread doesn't respawn on next hydrate.
        syncedThreadBodiesRef.current.delete(threadId);
        if (sessionContext?.sessionId) {
            fetch(`${API_URL}/carl/threads/${threadId}`, {
                method: 'DELETE',
                headers: { Authorization: sessionContext.sessionId },
            }).catch((deleteError) => {
                console.error('Ask Carl thread delete failed:', deleteError);
            });
        }
        if (activeThreadIdRef.current === threadId) {
            setActiveThreadId(null);
            setMessages([]);
        }
    }, [messagesKey, persistThreads, sessionContext?.sessionId, userId]);

    const clearMessages = useCallback(() => {
        // "Clear conversation" now starts a fresh thread; history stays in the drawer.
        startNewThread();
    }, [startNewThread]);

    const renameThread = useCallback((threadId: string, title: string) => {
        const trimmed = title.trim();
        if (!trimmed) return;
        updateThread(threadId, { title: trimmed, titleEdited: true });
    }, [updateThread]);

    /** Always create a fresh thread scoped to this order (drawer "New thread"). */
    const startNewOrderThread = useCallback((order: { orderId: string; roNumber?: string; vehicleName?: string }) => {
        streamAbortRef.current?.abort();
        const roLabel = order.roNumber ? `RO ${order.roNumber}` : 'Order';
        const thread: AskCarlThread = {
            threadId: uuidv4(),
            title: order.vehicleName ? `${roLabel} · ${order.vehicleName}` : roLabel,
            orderId: order.orderId,
            roNumber: order.roNumber,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
        persistThreads((previous) => [thread, ...previous]);
        setActiveThreadId(thread.threadId);
        setMessages([]);
        setPendingInputRequests([]);
        setError(null);
    }, [persistThreads]);

    /**
     * Select the most recently updated thread that referenced this order, or
     * create one titled with the RO when none exists. Never adopts an
     * unrelated active thread: doing so mis-tagged whatever conversation the
     * user last had open onto this RO (threads are only bound to an order by
     * being created for it, or by a tool result that resolves it —
     * scopeThreadToOrder).
     */
    const ensureOrderThread = useCallback((order: { orderId: string; roNumber?: string; vehicleName?: string }) => {
        const matches = threadsRef.current.filter((thread) => thread.orderId === order.orderId);
        if (matches.length) {
            const mostRecent = [...matches].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))[0];
            setActiveThreadId(mostRecent.threadId);
            return;
        }
        startNewOrderThread(order);
    }, [startNewOrderThread]);

    /**
     * Auto-title a thread once an order is resolved: "RO {ro} · {summary of the
     * first question}". Skips threads the user renamed manually.
     */
    const scopeThreadToOrder = useCallback((threadId: string, orderId?: string, roNumber?: string) => {
        const thread = threadsRef.current.find((entry) => entry.threadId === threadId);
        if (!thread) return;
        const patch: Partial<AskCarlThread> = {};
        if (orderId && thread.orderId !== orderId) {
            patch.orderId = orderId;
            if (roNumber) patch.roNumber = roNumber;
        }
        if (roNumber && !thread.titleEdited && !thread.title.startsWith(`RO ${roNumber}`)) {
            const baseSummary = thread.title === 'New conversation' || thread.title.startsWith('RO ')
                ? thread.title.replace(/^RO \S+( · )?/, '')
                : thread.title;
            patch.title = baseSummary ? `RO ${roNumber} · ${baseSummary}` : `RO ${roNumber}`;
        }
        if (Object.keys(patch).length) updateThread(threadId, patch);
    }, [updateThread]);

    /**
     * Replay the eve session's durable event stream and replace the local
     * message snapshot. Authoritative history lives server-side; localStorage
     * is only a cache that can miss UI cards if the client navigates away
     * mid-stream.
     */
    const reconcileThreadFromEve = useCallback(async (threadId: string, sessionId: string, authHeader: string) => {
        reconcileAbortRef.current?.abort();
        const abortController = new AbortController();
        reconcileAbortRef.current = abortController;

        try {
            const response = await fetch(`/eve/v1/session/${sessionId}/stream`, {
                headers: { Authorization: authHeader },
                signal: abortController.signal,
            });
            if (!response.ok || !response.body) return;

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            const events: EveStreamEvent[] = [];
            let buffered = '';
            const deadline = Date.now() + 10_000;
            let lastChunkAt = Date.now();
            const IDLE_MS = 1_500;

            while (Date.now() < deadline) {
                const readResult = await Promise.race([
                    reader.read(),
                    new Promise<'idle'>((resolve) => setTimeout(() => resolve('idle'), IDLE_MS)),
                ]);
                if (readResult === 'idle') {
                    if (Date.now() - lastChunkAt >= IDLE_MS) break;
                    continue;
                }
                if (readResult.done) break;
                lastChunkAt = Date.now();
                buffered += decoder.decode(readResult.value, { stream: true });
                const lines = buffered.split('\n');
                buffered = lines.pop() ?? '';
                for (const line of lines) {
                    if (!line.trim()) continue;
                    try {
                        events.push(JSON.parse(line));
                    } catch {
                        // skip malformed lines
                    }
                }
            }

            if (!events.length || activeThreadIdRef.current !== threadId) return;

            const { messages: folded, pendingInputRequests, lastEventAt } = foldEveEventsToMessages(events);
            if (!folded.length) return;

            if (lastEventAt > 0) lastEventAtRef.current.set(sessionId, lastEventAt);

            setMessages(folded);
            // Don't resurface a question the user already answered this session.
            setPendingInputRequests(
                pendingInputRequests.filter((request) => !answeredRequestIdsRef.current.has(request.requestId)),
            );

            for (const message of folded) {
                if (message.role !== 'assistant' || !message.ui) continue;
                const orderDetail = message.ui.orderDetail as Record<string, unknown> | undefined;
                if (orderDetail?.orderId) {
                    scopeThreadToOrder(
                        threadId,
                        orderDetail.orderId as string,
                        orderDetail.roNumber as string | undefined,
                    );
                    break;
                }
            }
        } catch (reconcileError) {
            if ((reconcileError as Error).name !== 'AbortError') {
                console.error('Ask Carl eve reconcile failed:', reconcileError);
            }
        }
    }, [scopeThreadToOrder]);

    // After hydrating from localStorage, sync from the eve session so UI cards
    // (part options, order context, etc.) survive navigation / drawer remounts.
    useEffect(() => {
        if (!activeThreadId || !sessionContext?.sessionId || isLoading) return undefined;
        // A question is live in the UI — replaying the session here would replace
        // the messages and drop the pending question card, forcing a re-ask.
        if (pendingInputRef.current.length) return undefined;
        const thread = threads.find((entry) => entry.threadId === activeThreadId);
        if (!thread?.sessionId || !thread?.continuationToken) return undefined;

        const threadId = activeThreadId;
        const sessionId = thread.sessionId;
        const authHeader = sessionContext.sessionId;
        const timer = window.setTimeout(() => {
            if (activeThreadIdRef.current === threadId) {
                reconcileThreadFromEve(threadId, sessionId, authHeader);
            }
        }, 0);

        return () => {
            window.clearTimeout(timer);
            reconcileAbortRef.current?.abort();
        };
    }, [activeThreadId, sessionContext?.sessionId, isLoading, threads, reconcileThreadFromEve]);

    // ── Streaming ──────────────────────────────────────────────────────────
    const consumeStream = useCallback(async (threadId: string, sessionId: string, authHeader: string, eventSinceMs: number) => {
        streamAbortRef.current?.abort();
        const abortController = new AbortController();
        streamAbortRef.current = abortController;

        const assistantId = uuidv4();
        let content = '';
        let ui: Record<string, unknown> | undefined;
        let inputRequests: AskCarlInputRequest[] | undefined;
        const toolEvents: AskCarlToolEvent[] = [];
        let assistantAppended = false;
        /**
         * Whether this read has folded in any of the current turn's activity.
         * The previous read stops AT input.requested, so the session.waiting
         * eve records right after the park is never marked as seen — on resume
         * it replays and would otherwise end this read before the new turn's
         * events (tool result, final message) arrive.
         */
        let sawTurnActivity = false;
        /**
         * Whether the current turn's `turn.completed` has landed. Guards the
         * done-state: a stale `session.waiting` replayed mid-turn must not end
         * the read while tools are still running — that froze the step
         * animations and showed done-state UI (feedback thumbs) before the
         * answer, which then popped in later via the reconcile pass.
         */
        let turnCompleted = false;

        const upsertAssistant = () => {
            const assistantMessage: AskCarlMessage = {
                id: assistantId,
                role: 'assistant',
                content,
                timestamp: new Date(),
                toolEvents: toolEvents.length ? [...toolEvents] : undefined,
                ui,
                inputRequests,
            };
            setMessages((prev) => {
                if (assistantAppended) {
                    return prev.map((message) => (message.id === assistantId ? assistantMessage : message));
                }
                assistantAppended = true;
                return [...prev, assistantMessage];
            });
        };

        const response = await fetch(`/eve/v1/session/${sessionId}/stream`, {
            headers: { Authorization: authHeader },
            signal: abortController.signal,
        });
        if (!response.ok || !response.body) {
            throw new Error(`Stream failed (HTTP ${response.status}).`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffered = '';

        const processEvent = (event: { type?: string; data?: Record<string, unknown>; meta?: { at?: string } }): boolean => {
            // The stream endpoint replays durable history; only fold in events
            // recorded after this POST.
            const recordedAt = event.meta?.at ? Date.parse(event.meta.at) : NaN;
            if (!Number.isNaN(recordedAt)) {
                if (recordedAt < eventSinceMs) return false;
                // Remember our position so the next resume of this session
                // doesn't re-ingest events we've already folded in.
                const seen = lastEventAtRef.current.get(sessionId) ?? 0;
                if (recordedAt > seen) lastEventAtRef.current.set(sessionId, recordedAt);
            }

            const data = (event.data ?? {}) as Record<string, unknown>;

            switch (event.type) {
                case 'actions.requested': {
                    sawTurnActivity = true;
                    const actions = (data.actions as Array<Record<string, unknown>>) ?? [];
                    for (const action of actions) {
                        const kind = action.kind as string;
                        const name = (action.toolName ?? action.subagentName ?? action.name ?? kind) as string | undefined;
                        if (!name || kind === 'load-skill') continue;
                        const callId = action.callId as string | undefined;
                        // Replay guard: don't duplicate a step we already show.
                        if (callId && toolEvents.some((toolEvent) => toolEvent.callId === callId)) continue;
                        toolEvents.push({
                            toolName: name,
                            callId,
                            timestamp: new Date().toISOString(),
                            summary: toolStepLabel(name),
                            status: 'running',
                        });
                    }
                    upsertAssistant();
                    return false;
                }
                case 'action.result': {
                    sawTurnActivity = true;
                    const result = data.result as Record<string, unknown> | undefined;
                    const callId = result?.callId as string | undefined;
                    const failed = data.status === 'failed' || result?.isError === true || Boolean(data.error);
                    const step = toolEvents.find((toolEvent) => toolEvent.callId && toolEvent.callId === callId);
                    if (step) step.status = failed ? 'failed' : 'completed';

                    const output = result?.output as Record<string, unknown> | undefined;
                    if (output && typeof output === 'object') {
                        const envelope = inferToolUiEnvelope(output);
                        if (envelope) {
                            const mapped = mapToolUiPayload(envelope);
                            if (mapped) {
                                ui = { ...(ui ?? {}), ...mapped };
                                const orderDetail = mapped.orderDetail as Record<string, unknown> | undefined;
                                if (orderDetail) {
                                    scopeThreadToOrder(
                                        threadId,
                                        orderDetail.orderId as string | undefined,
                                        orderDetail.roNumber as string | undefined,
                                    );
                                } else if (mapped.partOptions && output.orderId) {
                                    const partOptions = mapped.partOptions as Record<string, unknown>;
                                    scopeThreadToOrder(
                                        threadId,
                                        output.orderId as string,
                                        partOptions.roNumber as string | undefined,
                                    );
                                }
                            }
                        }

                        // Carl mutates order state out-of-band from website mutations —
                        // refresh the open order so tabs/activity stay in sync.
                        const toolName = String(result?.toolName ?? step?.toolName ?? '');
                        if (!failed && (
                            toolName === 'change_part_exclusion'
                            || toolName === 'bulk_change_part_exclusion'
                            || toolName === 'change_part_selection'
                            || toolName === 'toggle_part_flag'
                            || toolName === 'request_quotes'
                            || toolName === 'place_order'
                            || toolName === 'update_order_status'
                        )) {
                            const orderIdFromTool = typeof output.orderId === 'string' ? output.orderId : undefined;
                            const orderIdFromThread = threadsRef.current.find((thread) => thread.threadId === threadId)?.orderId;
                            const orderIdToRefresh = orderIdFromTool || orderIdFromThread;
                            if (orderIdToRefresh) {
                                void invalidateOrder(queryClient, orderIdToRefresh);
                            }
                        }
                    }
                    upsertAssistant();
                    return false;
                }
                case 'subagent.completed': {
                    const step = toolEvents.find((toolEvent) => toolEvent.toolName === 'support' && toolEvent.status === 'running');
                    if (step) step.status = 'completed';
                    upsertAssistant();
                    return false;
                }
                case 'message.completed': {
                    sawTurnActivity = true;
                    const text = data.message as string | null;
                    if (text) {
                        content = text;
                        upsertAssistant();
                    }
                    return false;
                }
                case 'input.requested': {
                    const requests = (data.requests as Array<Record<string, unknown>>) ?? [];
                    const mappedRequests = mapStreamInputRequests(requests)
                        // Replay guard: a request we already answered must not
                        // re-render its card or park the reader again.
                        .filter((request) => !answeredRequestIdsRef.current.has(request.requestId));
                    if (mappedRequests.length === 0) return false;
                    sawTurnActivity = true;
                    setPendingInputRequests(mappedRequests);
                    // Persist with the message so the question survives remounts/thread switches
                    inputRequests = mappedRequests;
                    // The question tool's step is resolved by the user's answer, not a result event
                    for (const step of toolEvents) {
                        if (step.toolName === 'ask_question' && step.status === 'running') step.status = 'completed';
                    }
                    upsertAssistant();
                    return true; // park until the user responds
                }
                case 'turn.failed':
                case 'session.failed': {
                    // Raw detail (data.message) stays in the console only.
                    console.error('[AskCarl/eve] failed event', event);
                    content = content || GENERIC_ERROR_MESSAGE;
                    for (const step of toolEvents) {
                        if (step.status === 'running') step.status = 'failed';
                    }
                    setError(GENERIC_ERROR_MESSAGE);
                    upsertAssistant();
                    return true;
                }
                case 'turn.completed':
                    // Only counts once this read has seen the current turn's
                    // activity — an ask_question park records the PREVIOUS
                    // turn's turn.completed after input.requested, so it
                    // replays here on resume and must not mark the new turn done.
                    if (sawTurnActivity) turnCompleted = true;
                    return false;
                case 'session.waiting':
                case 'session.completed':
                    // A stale park replay (recorded after the input.requested
                    // that ended the previous read) must not end this read
                    // before the resumed turn's events arrive. Only stop once
                    // this read has seen the current turn's activity AND its
                    // turn.completed. Ending on activity alone froze the tool
                    // animations and showed done-state UI (feedback thumbs)
                    // mid-turn; if turn.completed never arrives, the server
                    // closing the stream ends the read anyway.
                    return sawTurnActivity && turnCompleted;
                default:
                    return false;
            }
        };

        try {
            let done = false;
            while (!done) {
                const chunk = await reader.read();
                if (chunk.done) break;
                buffered += decoder.decode(chunk.value, { stream: true });
                const lines = buffered.split('\n');
                buffered = lines.pop() ?? '';
                for (const line of lines) {
                    if (!line.trim()) continue;
                    try {
                        if (processEvent(JSON.parse(line))) {
                            done = true;
                            break;
                        }
                    } catch (parseError) {
                        console.error('Ask Carl stream parse error:', parseError);
                    }
                }
            }
        } finally {
            abortController.abort();
        }

        // Any steps still marked running when the stream ends are done.
        let finalized = false;
        for (const step of toolEvents) {
            if (step.status === 'running') {
                step.status = 'completed';
                finalized = true;
            }
        }
        if ((finalized || (!assistantAppended && content))) upsertAssistant();
    }, [queryClient, scopeThreadToOrder]);

    const postTurn = useCallback(async (threadId: string, body: Record<string, unknown>) => {
        if (!sessionContext?.sessionId) return;
        const authHeader = sessionContext.sessionId;
        setIsLoading(true);
        setError(null);
        setPendingInputRequests([]);
        // The turn being posted answers/supersedes any stored question
        setMessages((prev) => prev.map((message) => (message.inputRequests ? { ...message, inputRequests: undefined } : message)));

        try {
            const thread = threadsRef.current.find((entry) => entry.threadId === threadId);
            const hasSession = Boolean(thread?.sessionId && thread?.continuationToken);
            const url = hasSession ? `/eve/v1/session/${thread!.sessionId}` : '/eve/v1/session';
            // Order-scoped threads (opened from OrderDetail) ride the order
            // reference along as ephemeral model context — but ONLY on turns
            // that carry a user message. On inputResponses-only turns (tool
            // approvals / question answers) eve appends context as a user
            // message AFTER the approval response, which pushes the approval
            // out of the last prompt message; the AI SDK then never executes
            // the approved tool, the model sees a stranded tool_use, Bedrock
            // rejects the request, and the approval card re-prompts forever.
            const clientContext = thread?.orderId && body.message !== undefined
                ? { scopedOrder: { orderId: thread.orderId, roNumber: thread.roNumber } }
                : undefined;
            const payload = {
                ...(hasSession ? { continuationToken: thread!.continuationToken } : {}),
                ...(clientContext ? { clientContext } : {}),
                ...body,
            };

            // Requests answered by this turn: replays of them must be ignored.
            const inputResponses = body.inputResponses as Array<{ requestId?: string }> | undefined;
            for (const response of inputResponses ?? []) {
                if (response.requestId) answeredRequestIdsRef.current.add(String(response.requestId));
            }

            // Resume from just past the last event we folded in for this
            // session; fall back to a wall-clock cushion for fresh sessions
            // (covers browser/server clock differences).
            const lastSeenAt = thread?.sessionId ? lastEventAtRef.current.get(thread.sessionId) ?? 0 : 0;
            const eventSinceMs = Math.max(lastSeenAt + 1, Date.now() - 5000);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: authHeader,
                },
                body: JSON.stringify(payload),
            });

            if (response.status === 401) {
                throw new Error('Your Yaxle session has expired. Please sign in again.');
            }
            if (!response.ok) {
                // Stale continuation token: detach the thread from the dead session
                if (hasSession && (response.status === 404 || response.status === 409 || response.status === 410)) {
                    updateThread(threadId, { sessionId: undefined, continuationToken: undefined });
                }
                throw new Error(`Ask Carl could not process that request (HTTP ${response.status}).`);
            }

            const responseBody = await response.json() as { sessionId?: string; continuationToken?: string };
            const sessionId = responseBody.sessionId
                ?? response.headers.get('x-eve-session-id')
                ?? thread?.sessionId;
            if (!sessionId) throw new Error('Ask Carl session could not be established.');
            updateThread(threadId, {
                sessionId,
                ...(responseBody.continuationToken ? { continuationToken: responseBody.continuationToken } : {}),
                updatedAt: new Date().toISOString(),
            });

            await consumeStream(threadId, sessionId, authHeader, eventSinceMs);
        } catch (requestError) {
            console.error('Ask Carl request failed:', requestError);
            // Session expiry is the one failure the user can act on; everything
            // else (stream/HTTP/model errors) gets the generic message.
            const sessionExpired = requestError instanceof Error
                && requestError.message.startsWith('Your Yaxle session has expired');
            const errorMessage = sessionExpired
                ? (requestError as Error).message
                : GENERIC_ERROR_MESSAGE;
            setError(errorMessage);
            setMessages((prev) => [
                ...prev,
                {
                    id: uuidv4(),
                    role: 'assistant',
                    content: errorMessage,
                    timestamp: new Date(),
                    error: errorMessage,
                },
            ]);
        } finally {
            setIsLoading(false);
        }
    }, [consumeStream, sessionContext?.sessionId, updateThread]);

    /**
     * Swap the verbatim placeholder title for a model-written synopsis of the
     * first exchange (POST /carl/thread-title → Bedrock Sonnet). Fire-and-
     * forget: any failure keeps the placeholder. Skips threads the user
     * renamed while the request was in flight, and preserves the "RO {ro} · "
     * prefix on order-scoped threads.
     */
    const generateThreadTitle = useCallback(async (threadId: string, question: string) => {
        if (!sessionContext?.sessionId) return;
        const answer = [...messagesRef.current].reverse()
            .find((message) => message.role === 'assistant' && !message.error && message.content.trim())?.content;
        try {
            const response = await fetch(`${API_URL}/carl/thread-title`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: sessionContext.sessionId },
                body: JSON.stringify({
                    question,
                    ...(answer ? { response: answer.slice(0, 1500) } : {}),
                }),
            });
            if (!response.ok) return;
            const body = await response.json() as { title?: string };
            const title = body.title?.trim();
            if (!title) return;
            const thread = threadsRef.current.find((entry) => entry.threadId === threadId);
            if (!thread || thread.titleEdited) return;
            updateThread(threadId, {
                title: thread.roNumber && thread.title.startsWith(`RO ${thread.roNumber}`)
                    ? `RO ${thread.roNumber} · ${title}`
                    : title,
            });
        } catch (titleError) {
            console.error('Ask Carl thread title generation failed:', titleError);
        }
    }, [sessionContext?.sessionId, updateThread]);

    const sendMessage = useCallback(async (query: string) => {
        const text = query.trim();
        if (!text || !sessionContext?.sessionId || isLoading) return;

        // A tool-approval gate is pending: a plain message can't answer it and
        // would make eve defer + re-emit the same gate. Ignore it — the user
        // must use the Approve/Deny buttons (the composer is disabled in the UI).
        if (pendingInputRef.current.some(isApprovalRequest)) return;

        // Ensure a thread exists; give it an instant placeholder title from
        // the first message, replaced by a generated synopsis after the turn.
        let threadId = activeThreadIdRef.current;
        let needsGeneratedTitle = false;
        const existing = threadId ? threadsRef.current.find((thread) => thread.threadId === threadId) : undefined;
        if (!threadId || !existing) {
            threadId = uuidv4();
            const thread: AskCarlThread = {
                threadId,
                title: summarizeTitle(text),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            persistThreads((previous) => [thread, ...previous]);
            // The activeThreadId-change effect must not wipe the in-flight
            // first message / stream for this brand-new thread.
            skipMessagesLoadRef.current = threadId;
            setActiveThreadId(threadId);
            needsGeneratedTitle = true;
        } else if (existing.title === 'New conversation' && !existing.titleEdited) {
            updateThread(threadId, { title: summarizeTitle(text) });
            needsGeneratedTitle = true;
        } else if (existing.orderId && existing.roNumber && !existing.titleEdited && !existing.title.includes('·')) {
            // Order-scoped thread getting its first question: append the summary
            updateThread(threadId, { title: `RO ${existing.roNumber} · ${summarizeTitle(text)}` });
            needsGeneratedTitle = true;
        }

        setMessages((prev) => [
            ...prev,
            { id: uuidv4(), role: 'user', content: text, timestamp: new Date() },
        ]);

        // A typed reply while a question is pending answers that question
        // (freeform text), rather than being queued as an unrelated message.
        const pendingQuestion = pendingInputRef.current.find(
            (request) => request.display !== 'confirmation' && request.allowFreeform !== false,
        );
        if (pendingQuestion) {
            setPendingInputRequests([]);
            await postTurn(threadId, { inputResponses: [{ requestId: pendingQuestion.requestId, text }] });
            return;
        }

        await postTurn(threadId, { message: text });
        if (needsGeneratedTitle) void generateThreadTitle(threadId, text);
    }, [generateThreadTitle, isLoading, persistThreads, postTurn, sessionContext?.sessionId, updateThread]);

    const respondToInput = useCallback(async (requestId: string, response: { optionId?: string; text?: string }) => {
        const threadId = activeThreadIdRef.current;
        if (!threadId || (!response.optionId && !response.text)) return;

        // Persist the user's answer in the thread so the choice reads back in history.
        const request = pendingInputRef.current.find((entry) => entry.requestId === requestId);
        const optionLabel = response.optionId
            ? request?.options?.find((option) => option.id === response.optionId)?.label ?? response.optionId
            : undefined;
        const answerText = response.text ?? optionLabel;
        if (answerText) {
            setMessages((prev) => [
                ...prev,
                { id: uuidv4(), role: 'user', content: answerText, timestamp: new Date() },
            ]);
        }

        setPendingInputRequests([]);
        await postTurn(threadId, { inputResponses: [{ requestId, ...response }] });
    }, [postTurn]);

    /**
     * Rate an assistant response. Thumbs-down sends the thread context to the
     * feedback endpoint (custom eve channel at /eve/feedback), which replays
     * the durable session server-side and reports it to Slack. A compact
     * client-side transcript rides along as a fallback in case the eve
     * session can't be replayed.
     */
    const submitFeedback = useCallback(async (
        messageId: string,
        rating: AskCarlFeedbackRating,
        details?: AskCarlFeedbackDetails,
    ): Promise<boolean> => {
        if (!sessionContext?.sessionId) return false;
        const threadId = activeThreadIdRef.current;
        const thread = threadId ? threadsRef.current.find((entry) => entry.threadId === threadId) : undefined;

        const clientTranscript = messagesRef.current.flatMap((message) => {
            const lines: string[] = [];
            for (const toolEvent of message.toolEvents ?? []) {
                lines.push(`🔧 ${toolEvent.toolName} → ${toolEvent.status ?? 'unknown'}`);
            }
            if (message.content.trim()) {
                const speaker = message.role === 'user' ? '👤 User' : '🤖 Carl';
                lines.push(`${speaker}: ${message.content.slice(0, 1200)}`);
            }
            return lines;
        });

        // Settle the buttons immediately; feedback UX shouldn't wait on Slack.
        setMessages((prev) => prev.map((message) => (
            message.id === messageId ? { ...message, feedback: rating } : message
        )));

        try {
            const response = await fetch('/eve/feedback', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: sessionContext.sessionId,
                },
                body: JSON.stringify({
                    rating,
                    reasons: details?.reasons,
                    comment: details?.comment,
                    messageId,
                    threadId: thread?.threadId,
                    threadTitle: thread?.title,
                    orderId: thread?.orderId,
                    roNumber: thread?.roNumber,
                    eveSessionId: thread?.sessionId,
                    clientTranscript,
                }),
            });
            return response.ok;
        } catch (feedbackError) {
            console.error('Ask Carl feedback failed:', feedbackError);
            return false;
        }
    }, [sessionContext?.sessionId]);

    const latestUiPayload = useMemo(() => {
        for (let index = messages.length - 1; index >= 0; index -= 1) {
            const message = messages[index];
            if (message.role === 'assistant' && message.ui) {
                return message.ui;
            }
        }
        return undefined;
    }, [messages]);

    return {
        messages,
        isLoading,
        error,
        sendMessage,
        clearMessages,
        latestUiPayload,
        pendingInputRequests,
        respondToInput,
        threads,
        activeThreadId,
        startNewThread,
        selectThread,
        deleteThread,
        renameThread,
        ensureOrderThread,
        startNewOrderThread,
        submitFeedback,
    };
};

export default useAskCarlChat;
